import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { KEService } from '../../ke.service';
import { MatDialogConfig } from '@angular/material/dialog';
import { Subscription, Observable, firstValueFrom } from 'rxjs';
import { FormStatusEnum, Roles, WFSaveOptions, FormType, OlogNotificationType, Tag } from 'src/app/common/enumerations/enumerations';
import { WFComponent, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { PartialOlogComponent } from 'src/app/components/olog/partial-olog/partial-olog.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { OLogService } from 'src/app/components/olog/olog.service';
import { Resource, ShutterResource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { WfSignatureResource, WfSignature, WFTaskSignatureResource, SaveTaskResource } from 'src/app/components/workflow/workflow';
import { KEResource, EpicsValuesResource, SaveKEStatusResource } from '../../ke';
import { BaseComponent } from 'src/app/common/base/base.component';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { KEnableUpdate } from '../../store/ke/kenable.action';
import { PV } from 'src/app/services/pv-info/pv-info';
import { Document } from 'src/app/services/documents/documents';
import { SCFMaster } from 'src/app/components/scf-v2/scf';
import { Checklist, Option } from 'src/app/components/checklists/checklists';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { Tooltip } from 'src/app/common/tooltip';
import moment from 'moment';
import { PVRefresh } from 'src/app/services/pv-info/store/pv-info.action';
import { RSF } from 'src/app/components/rsf/rsf';
import { ConditionParser } from 'src/app/modules/libs/condition-parser';

@Component({
  selector: 'ke-sectionX',
  templateUrl: './ke-sectionX.component.html',
  styleUrls: ['./ke-sectionX.component.scss']
})

export class KESectionXComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  @Input() section!: number;
  @Input() ke?: KEResource | null;

  @Output() requestRefresh = new EventEmitter();
  @Output() refreshVacuum = new EventEmitter();

  loading!: boolean;

  epicsValues?: EpicsValuesResource[];
  epicsValues$!: Observable<EpicsValuesResource[]>;
  epicaValiesSubs!: Subscription;

  shutters!: ShutterResource[];
  shutters$!: Observable<ShutterResource[]>;
  shuttersSubs!: Subscription;

  scfMasters?: SCFMaster[];
  scfMasters$!: Observable<SCFMaster[]>;
  scfMastersSubs!: Subscription;

  rsfs?: RSF[];
  rsfs$!: Observable<RSF[]>;
  rsfsSubs!: Subscription;

  checklists?: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;

  pvs?: PV[];
  pvs$!: Observable<PV[]>;
  pvsSubs!: Subscription;
  documents?: Document[];
  documents$!: Observable<Document[]>;
  documentsSubs!: Subscription;
  brcRestrictedShutters!: ShutterResource[];

  resourceID?: number;
  resources!: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  isVacuumSection = false;
  isShieldingsSection = false;
  isKeStatus = false
  abbreviated!: boolean;


  completeActive!: boolean;
  completeShieldings!: boolean;
  shieldingsUserId?: number;

  sectionName!: string | null;

  wfSignatures!: WfSignatureResource[];
  signatures!: WfSignature[];
  wfTaskSignature: WFTaskSignatureResource = new WFTaskSignatureResource();

  approvals!: number;

  disabled!: boolean;
  radioDisabled!: boolean;

  ologButton = false;

  components!: WFComponent[];
  components$!: Observable<WFComponent[]>;
  componentSubs!: Subscription;

  identificator!: number;

  constructor(
    protected override injector: Injector,
    private _ke: KEService,
    private _olog: OLogService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ngOnInit();
    if (this.ke?.id == 0 && this.ke.status == FormStatusEnum.New) {
      this.create();
    }
    else if (this.ke?.id) {
      this.refresh(this.ke);
    }
  }

  override ngOnDestroy(): void {
    this.epicaValiesSubs?.unsubscribe();
    this.componentSubs?.unsubscribe();
    this.pvsSubs?.unsubscribe();
    this.scfMastersSubs?.unsubscribe();
    this.checklistsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnInit() {
    this.loadPVs();
    this.loadChecklists();
    this.loadComponents();
    this.loadShutters();
    this.loadDocuments();
    this.loadResources();
    this.loadSCFMasters();
    this.loadRSFs();
  }

  loadChecklists() {
    this.checklists$ = this.store.select(state => state.Checklists.data);
    this.checklistsSubs = this.checklists$.subscribe(data => {
      if (data?.length) {
        this.checklists = data;
      }
    });
  }

  loadSCFMasters() {
    this.scfMasters$ = this.store.select(state => state.SCFMasters.data);
    this.scfMastersSubs = this.scfMasters$.subscribe(data => {
      if (data?.length) {
        this.scfMasters = data;
      }
    });
  }

  loadRSFs() {
    this.rsfs$ = this.store.select(state => state.RSFs.data);
    this.rsfsSubs = this.rsfs$.subscribe(data => {
      if (data?.length) {
        this.rsfs = data;
      }
    });
  }

  loadPVs() {
    this.pvs$ = this.store.select(state => state.PV.data);
    this.pvsSubs = this.pvs$.subscribe(data => {
      if (data?.length) {
        this.pvs = data;
        this.loadEpicsValues();
      }
    });
  }

  loadComponents() {
    this.components$ = this.store.select(state => state.Components.data);
    this.componentSubs = this.components$.subscribe(data => {
      this.components = data.filter(x => x.status === 1).sort((a, b) => this.utils.sort(a.name, b.name));
    });
  }

  loadShutters() {
    this.shutters$ = this.store.select(state => state.Resources.shutters);
    this.shuttersSubs = this.shutters$.subscribe(data => {
      this.shutters = data;
    });
  }

  loadDocuments() {
    this.documents$ = this.store.select(state => state.Documents.data);
    this.documentsSubs = this.documents$.subscribe(data => {
      if (data?.length) {
        this.documents = data;
      }
    });
  }

  loadResources() {
    this.resources$ = this.store.select(state => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe(data => {
      if (data?.length != 0) {
        this.resources = data;
        const restrictedResources = this.resources.filter(x => x.resourceType?.id == this.resourceTypeEnum.Beamline && x.keRestricted).map(r => r.reviewRestrictedResourceID);
        this.brcRestrictedShutters = this.shutters.filter(s => restrictedResources.includes(s.id));
      }
    });
  }

  create() {
    this.wfSignatures = [];
    this.signatures = [];
    this.sectionName = null;
    this.isVacuumSection = false;
  }

  async refresh(ke: KEResource | null = null): Promise<void> {
    this.initializeRefreshState(ke);

    if (!this.ke?.wfSignatures?.length) {
      this.sectionName = null;
      return;
    }

    this.wfSignatures = this.ke.wfSignatures.filter(
      signature => signature.sectionNumber === this.section
    );

    if (!this.wfSignatures.length) {
      this.sectionName = null;
      this.approvals = 0;
      return;
    }

    this.setShieldingsUserId();
    this.setSectionMetadata();
    this.checkConditions();

    this.signatures = await Promise.all(
      this.wfSignatures.map(signature => this.getSignature(signature, this.section))
    );

    this.renumberArray();

    if (this.ke.status === FormStatusEnum.Draft) {
      await this.refreshAndSaveVacuum();
    }
  }

  private initializeRefreshState(ke: KEResource | null): void {
    this.ke = ke ?? {
      id: 0,
      wfSignatures: [],
      serialNo: '',
      status: 0
    } as KEResource;

    this.signatures = [];
    this.resourceID = this.ke.resource?.childResources?.find(r => r.childResourceType === this.resourceTypeEnum.Beamline)?.childResourceID ?? undefined;
    this.abbreviated = this.ke.type === 0;
  }

  private setShieldingsUserId(): void {
    const shouldResolveShieldingsUser =
      (this.ke?.status === FormStatusEnum.KeyEnable && this.section === 5) ||
      (this.ke?.status === FormStatusEnum.Shielding && this.section === 4);

    if (!shouldResolveShieldingsUser) {
      return;
    }

    this.shieldingsUserId = this.ke?.wfSignatures
      ?.find(signature => signature.sectionNumber === 4 && signature.signed)
      ?.signedBy?.id;
  }

  private setSectionMetadata(): void {
    const firstSignature = this.wfSignatures[0];
    if (!firstSignature) {
      this.sectionName = null;
      this.approvals = 0;
      return;
    }

    this.setDisabled(firstSignature);
    this.sectionName = firstSignature.sectionName ?? null;
    this.approvals = this.wfSignatures.length;
  }

  async getSignature(t: WfSignatureResource, section: number): Promise<WfSignature> {
    this.setSectionFlags(section);

    const vacuum = await this.resolveVacuumData(t, section);
    const moreInfo = t.moreInfo ? this.formatMoreInfo(t.moreInfo) : undefined;
    const locationMoreInfo = t.locationMoreInfo ? this.formatMoreInfo(t.locationMoreInfo) : undefined;

    const isTaskDisabled = this.setDisabledTask(t);
    const isDisabled = isTaskDisabled || !!t.disabled || !!this.disabled;

    return {
      id: t.id,
      number: t.sectionNumber.toString(),
      approveActionId: t.approveID,
      unapproveActionId: t.unapproveID,
      disapproveActionId: t.disapproveID,
      amendmentId: t.amendmentID,
      name: t.titleBefore,
      question: t.question,
      question2: t.titleAfter,
      question3: t.titleAfter2,
      roles: t.roles.map(r => r?.id),
      roleCodes: t.roles.map(r => r?.code).join('/'),
      disabled: isDisabled,
      radioDisabled: this.disableRadio(t),
      approvedBy: t.signedBy,
      approved: t.signed,
      approvedOn: t.signedOn,
      approveAction: !t.signedBy && t.approveID !== 0,
      unapproveAction: !t.signedBy && t.unapproveID !== 0,
      disapproveAction: t.disapproveEnabled,
      amendment: t.amendmentEnabled,
      required: t.required,
      taskId: t.taskID,
      value: t.value,
      value2: t.numericValue,
      textValue: this.getSignatureTextValue(t, vacuum),
      type: t.type,
      options: this.formatOptions(t),
      sectionName: t.sectionName,
      isVacuum: !!vacuum,
      disableEdit: this.getDisableEdit(t, vacuum, isTaskDisabled),
      visible: t.visible,
      enabled: vacuum ? !!t.enabled : true,
      warning: this.getWarningValue(t, vacuum),
      moreInfo,
      locationMoreInfo: this.hidesLocationWarning() ? undefined : locationMoreInfo,
      scheduleTypeId: t.scheduleType,
      scheduleResourceId: t.scheduleResourceId,
      dateValue1: t.dateValue1,
      dateValue2: t.dateValue2,
      dateValue3: this.normalizeDateValue(t.dateValue3),
      scheduleName: t.scheduleName,
      locationName: t.locationName,
      condition: t.condition,
      configuration: t.configuration,
      tooltips: vacuum ? this.getVacuumTooltip(t) : undefined
    };
  }

  private setSectionFlags(section: number): void {
    this.isVacuumSection = section === 3;
    this.isShieldingsSection = section === 4;
    this.isKeStatus =
      section === 5 && this.ke?.status === FormStatusEnum.KeyEnable;
    this.ologButton = section === 5;
  }

  private async resolveVacuumData(
    t: WfSignatureResource,
    section: number
  ): Promise<any | null> {
    const isVacuumSection = section === 3;
    const usesEpics = !!t.text && t.text.includes('EPICS');

    if (!isVacuumSection || !usesEpics) {
      return null;
    }

    const vacuum = await this.getEpicsValue(t);
    t.warning = !vacuum?.isValid;
    t.enabled = t.doubleValue !== 0 && !t.warning;

    return vacuum;
  }

  private getSignatureTextValue(t: WfSignatureResource, vacuum: any | null): string {
    if (vacuum) {
      return vacuum.question ?? '';
    }

    return t.text ? t.text.replace('^', '<sup>') : '';
  }

  private getDisableEdit(
    t: WfSignatureResource,
    vacuum: any | null,
    isTaskDisabled: boolean
  ): boolean {
    if (vacuum) {
      return !this.hasRoles([Roles.FO, Roles.VAC]) || !!this.disabled || !!t.signed;
    }

    return !!this.disabled || isTaskDisabled;
  }

  // Yellow (location-only) caution warnings are frozen once a KE reaches Key Enable,
  // Handshake, or Closed: forms activated after Shielding must not interfere with a KE
  // that has already advanced, so we hide the stale caution icon in those statuses.
  private hidesLocationWarning(): boolean {
    return (
      this.ke?.status === FormStatusEnum.KeyEnable ||
      this.ke?.status === FormStatusEnum.Handshake ||
      this.ke?.status === FormStatusEnum.Closed
    );
  }

  private getWarningValue(
    t: WfSignatureResource,
    vacuum: any | null
  ): boolean {
    if (this.ke?.status === FormStatusEnum.Closed) {
      return false;
    }

    if (vacuum) {
      return !vacuum.isValid;
    }

    return !!t.warning;
  }

  private normalizeDateValue(dateValue?: Date): Date | undefined {
    return dateValue?.toString() !== '0001-01-01T00:00:00'
      ? dateValue
      : undefined;
  }

  getVacuumTooltip(t: WfSignatureResource): Tooltip[] | undefined {
    const options = this.utils.JSONparse(t.options) as KEOptions;
    const tooltip = {} as Tooltip;
    if (options && t.doubleValue) {
      tooltip.action = options.option;
      tooltip.content = (`<b>${options.user.name ?? ''}</b><br><span class="date">${moment(options.date).format("MMM Do YYYY [at] h:mm:ss a")}</span>`);
    }
    return [tooltip];
  }

  disableRadio(t: WfSignatureResource) {
    if ((t.condition == '!SCFActive' || t.condition == '!RSFActive') && t.numericValue == 1) {
      return true;
    }
    else return false;
  }

  /**
   * Adds Rad Survey form details to the applicable SCF/RSF option, including location-only conditions.
   * @param t Workflow signature whose options and Rad Survey state are being rendered.
   */
  formatOptions(t: WfSignatureResource): Option[] {
    const options = this.parseOptions(t.options);
    if (!options.length) {
      return [];
    }

    const shouldDecorateYesOption =
      (t.condition === '!SCFActive' || t.condition === '!RSFActive' ||
        t.condition === '!SCFActiveLoc' || t.condition === '!RSFActiveLoc') &&
      t.numericValue === 1;

    if (!shouldDecorateYesOption) {
      return options;
    }

    const requiredForms = this.parseRadSurveyRequiredForms(t.radSurveyRequiredForms);
    if (!requiredForms.length) {
      return options;
    }

    return options.map(option =>
      option.value === 1
        ? { ...option, radSurveyRequiredForms: requiredForms }
        : option
    );
  }

  private parseOptions(optionsJson?: string): Option[] {
    if (!optionsJson) {
      return [];
    }

    try {
      const parsed = this.utils.JSONparse(optionsJson) as Option[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private parseRadSurveyRequiredForms(formsJson?: string | null): AffectedForm[] {
    if (!formsJson) {
      return [];
    }

    try {
      const parsed = this.utils.JSONparse(formsJson) as AffectedForm[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  formatMoreInfo(moreInfo: string): string {
    if (!moreInfo) {
      return '';
    }

    this.ensureDocumentsLoaded();

    const forms = this.utils.JSONparse(moreInfo) as string[] | null;
    if (!forms?.length) {
      return '';
    }

    if (!this.containsSupportedFormType(moreInfo)) {
      return moreInfo;
    }

    const documents = this.documents?.filter(d => forms.includes(d.serialNo as string)) ?? [];
    if (!documents.length) {
      return '';
    }

    return `
    <div class="d-flex flex-column">
      ${documents.map(d => this.buildDocumentHtml(d)).join('')}
    </div>
  `;
  }

  // Prepends an explanatory line to the formatted forms list for the checkbox
  // tooltips only. The plain formatMoreInfo output is still used for the confirm
  // dialog (which supplies its own banner text), so the message stays here.
  restrictionTooltip(forms?: string | null): string {
    return this.buildFormsTooltip(forms, 'The following forms were found that prohibit this resource:');
  }

  locationTooltip(forms?: string | null): string {
    return this.buildFormsTooltip(forms, "The following forms were found that affect this resource but don't prohibit it:");
  }

  private buildFormsTooltip(forms: string | null | undefined, message: string): string {
    if (!forms) {
      return '';
    }

    return `<div class="mb-1">${message}</div>${forms}`;
  }

  private ensureDocumentsLoaded(): void {
    if (!this.documents) {
      this.loadDocuments();
    }
  }

  private containsSupportedFormType(moreInfo: string): boolean {
    const supportedPrefixes = ['SCF-', 'PPSTB-', 'EB-', 'RSSWA-', 'RSF-'];
    return supportedPrefixes.some(prefix => moreInfo.includes(prefix));
  }

  private buildDocumentHtml(d: any): string {
    const serialNo = d.serialNo ?? '';
    const linkOrLabel = d.disableLink
      ? `<span class="yellow">${serialNo}</span>`
      : `<a href="#/redirect/${serialNo}">${serialNo}</a>`;

    return `<div class="my-1 bold ${d.cssClass}">${linkOrLabel}</div>`;
  }

  ologPermission() {
    const FO = this.hasRoles([Roles.FO]);
    if (!this.ologButton || !FO || this.ke?.status != FormStatusEnum.KeyEnable) return true;
    else return false;
  }

  setDisabled(t: WfSignatureResource): boolean {
    this.disabled = this.computeDisabled(t);
    return this.disabled;
  }

  private computeDisabled(t: WfSignatureResource): boolean {
    if (!this.currentUser) {
      return true;
    }

    if (
      this.hasRoles([Roles.FO]) &&
      this.section === 4 &&
      this.ke?.status === FormStatusEnum.Shielding
    ) {
      const signedRecord = this.wfSignatures.find(s => s.signed);
      return !!signedRecord && signedRecord.signedBy?.id !== this.currentUser.id;
    }

    if (
      this.hasRoles([Roles.FO]) &&
      this.section === 5 &&
      this.ke?.status === FormStatusEnum.KeyEnable
    ) {
      return this.shieldingsUserId !== this.currentUser.id;
    }

    if (
      this.hasRoles([Roles.BLSCI, Roles.ALTBLSCI]) &&
      this.section === 6 &&
      this.ke?.status === FormStatusEnum.Handshake
    ) {
      return false;
    }

    if (
      this.hasRoles([Roles.FO, Roles.BRC]) &&
      this.section === 1 &&
      (
        this.ke?.status === FormStatusEnum.Draft ||
        this.ke?.status === FormStatusEnum.Active
      )
    ) {
      return false;
    }

    return t.status !== this.ke?.status;
  }

  disableButtons() {
    const enable = (this.ke?.status === FormStatusEnum.Active || this.ke?.status === FormStatusEnum.Shielding) && this.hasRoles([Roles.FO, Roles.VAC]);
    return !enable;
  }

  //Determine if task is disabled
  setDisabledTask(t: WfSignatureResource) {
    let enable = false;
    if (t.users)
      enable = t.users.map(u => u.id).includes(this.currentUser?.id) && this.hasRoles(t.roles) && this.validateWarningValues(t);
    else
      enable = this.hasRoles(t.roles) && this.validateWarningValues(t);
    return !enable;
  }

  //See if form or BRC restricts KE
  validateWarningValues(t: WfSignatureResource): boolean {
    if (!t.warning) {
      return true;
    }

    if (this.section === 1 && t.condition === '!BRCRestriction') {
      return false;
    }

    const hasMoreInfo = !!t.moreInfo && t.moreInfo !== '[]';
    if (!hasMoreInfo) {
      return true;
    }

    if (this.section === 1 && this.shouldHideSection1Warning(t)) {
      return false;
    }

    if (this.section === 3 && !this.hasRoles([Roles.VAC])) {
      return false;
    }

    return true;
  }

  private shouldHideSection1Warning(t: WfSignatureResource): boolean {
    const requiresMissingConfiguration =
      (t.condition === '!SCFActive' || t.condition === '!RSFActive') &&
      !t.configuration;

    const restrictedConditions = [
      '!RSSWAActive',
      '!PPSTBActive',
      '!EBActive',
    ];

    return requiresMissingConfiguration || restrictedConditions.includes(t.condition ?? '');
  }

  renumberArray(): void {
    let displayIndex = 1;
    let order = 1;

    this.loadComponents();

    const componentMap = new Map(
      this.components?.map(component => [component.id, component]) ?? []
    );

    this.signatures
      .sort((a, b) => this.utils.sort(a.number, b.number))
      .forEach(signature => {
        const control = componentMap.get(signature.type ?? 0);

        signature.order = order++;

        if (signature.required && control?.numericHeader) {
          signature.number = `${this.section}.${displayIndex++}`;
        } else {
          signature.number = null;
        }
      });
  }

  async checkOrChange(
    e: any,
    taskId?: number,
    approveId?: number,
    unapproveId?: number,
    condition?: string,
    moreInfo?: string,
    configuration?: string,
    isVacuum?: boolean,
    type?: number
  ): Promise<void> {
    const isVacuumNumericChange = isVacuum && typeof e !== 'boolean';

    if (isVacuumNumericChange) {
      const wfSignature = this.wfSignatures.find(s => s.taskID === taskId);
      await this.change(wfSignature?.doubleValue ?? 0, taskId, type, true);
      return;
    }

    await this.check(e, taskId, approveId, unapproveId, condition, moreInfo, configuration);
  }

  async check(
    checked: boolean,
    taskId?: number,
    approveId?: number,
    unapproveId?: number,
    condition?: string,
    moreInfo?: string,
    configuration?: string
  ): Promise<void> {
    const wfSignature = this.wfSignatures.find(s => s.taskID === taskId);
    if (!wfSignature) {
      return;
    }

    if (checked) {
      await this.handleCheckedSignature(
        wfSignature,
        taskId,
        approveId,
        condition,
        moreInfo,
        configuration
      );
      return;
    }

    await this.handleUncheckedSignature(wfSignature, taskId, unapproveId);
  }

  async change(
    e: number,
    taskId?: number,
    type?: number,
    isVacuum?: boolean
  ): Promise<void> {
    const wfSignature = this.wfSignatures.find(s => s.id === taskId);
    if (!wfSignature || !taskId) {
      return;
    }

    switch (type) {
      case 6:
        await this.handleRadioChange(wfSignature, taskId, e);
        return;

      case 8:
        if (isVacuum) {
          await this.handleVacuumNumericChange(wfSignature, taskId, e);
        } else {
          await this.handleTextInputChange(wfSignature, taskId, e);
        }
        return;

      default:
        await this.handleDefaultTextChange(wfSignature, taskId, e);
        return;
    }
  }

  private async handleCheckedSignature(
    wfSignature: WfSignatureResource,
    taskId?: number,
    approveId?: number,
    condition?: string,
    moreInfo?: string,
    configuration?: string
  ): Promise<void> {
    const requiresWarningConfirmation =
      this.shouldShowCheckWarning(condition, moreInfo, configuration);

    if (requiresWarningConfirmation) {
      const confirmed = await this.confirmCheckWarning(condition!, moreInfo!);

      if (!confirmed) {
        this.signatures = this.utils.cloneDeep(this.signatures);
        return;
      }
    }

    await this.signSignature(wfSignature, taskId, approveId);
  }

  private async handleUncheckedSignature(
    wfSignature: WfSignatureResource,
    taskId?: number,
    unapproveId?: number
  ): Promise<void> {
    await this.unapprove(taskId, unapproveId);

    if (wfSignature.component?.hasRadioButtons) {
      wfSignature.numericValue = 0;

      const saveTask: SaveTaskResource = {
        id: wfSignature.id,
        numericValue: 0,
        field: 'numericValue',
        option: WFSaveOptions.MANUAL
      };

      await this.saveTask(saveTask);
    }

    this.unsignSignature(wfSignature);
    this.refresh(this.ke);
  }

  private shouldShowCheckWarning(
    condition?: string,
    moreInfo?: string,
    configuration?: string
  ): boolean {
    if (!moreInfo) {
      return false;
    }

    return (
      condition === '!PPSTBActiveLoc' ||
      condition === '!EBActiveLoc' ||
      condition === '!RSSWAActiveLoc' ||
      condition === '!SCFActiveLoc' ||
      condition === '!RSFActiveLoc' ||
      ((condition === '!SCFActive' || condition === '!RSFActive') && !!configuration)
    );
  }

  private async confirmCheckWarning(
    condition: string,
    moreInfo: string
  ): Promise<boolean> {
    const messageKey =
      condition === '!PPSTBActiveLoc'
        ? 'KE_PPSTBActive'
        : condition === '!EBActiveLoc'
          ? 'KE_EBActive'
          : condition === '!RSSWAActiveLoc'
            ? 'KE_RSSWAActive'
            : condition === '!RSFActiveLoc' || condition === '!RSFActive'
              ? 'KE_RSFActive'
              : 'KE_SCFActive';

    const message = this.getMessage(messageKey);

    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '600px',
      data: {
        message: message.description.replace('{forms}', `<b class="no-wrap">${moreInfo}</b>`),
        icon: 'warning',
        changeButtonLabels: true,
        labelButtonCancel: 'Cancel',
        labelButtonAccept: 'Accept'
      }
    });

    return !!(await dialogRef.afterClosed().toPromise());
  }

  private async signSignature(
    wfSignature: WfSignatureResource,
    taskId?: number,
    approveId?: number,
    saveTask?: SaveTaskResource
  ): Promise<void> {
    if (!wfSignature.wfTaskLocal.wfTasks?.length) {
      return;
    }

    wfSignature.signed = true;
    wfSignature.signedBy = this.currentUser;
    wfSignature.signedOn = new Date();
    wfSignature.wfTaskLocal.doubleValue = wfSignature.doubleValue ?? 0;
    wfSignature.wfTaskLocal.wfTasks[0].signedBy = {
      id: this.currentUser?.id,
      name: this.currentUser?.name,
      initials: this.currentUser?.initials
    };

    await this.approve(taskId, approveId, saveTask);
    this._ke.updateTask(wfSignature.wfTaskLocal);
  }

  private unsignSignature(wfSignature: WfSignatureResource): void {
    if (!wfSignature.wfTaskLocal.wfTasks?.length) {
      return;
    }

    wfSignature.signed = false;
    wfSignature.signedBy = null;
    wfSignature.signedOn = null;
    wfSignature.wfTaskLocal.wfTasks[0].signedBy = null;
    this._ke.updateTask(wfSignature.wfTaskLocal);
  }

  private async handleRadioChange(
    wfSignature: WfSignatureResource,
    taskId: number,
    value: number
  ): Promise<void> {
    const oldValue = wfSignature.numericValue;

    const saveTask: SaveTaskResource = {
      id: taskId,
      numericValue: value,
      field: 'numericValue',
      option: WFSaveOptions.MANUAL
    };

    if (wfSignature.signed && wfSignature.numericValue !== value) {
      wfSignature.numericValue = value;

      const confirmed = await this.confirmWarning(
        'KERadioChangedWarning',
        'warn',
        '400px'
      );

      if (confirmed && wfSignature.unapproveID) {
        saveTask.numericValue = 0;
        await this.saveTask(saveTask);
        await this.unapprove(wfSignature.taskID, wfSignature.unapproveID);
        return;
      }

      const original = this.ke?.wfSignatures?.find(x => x.id === wfSignature.id);
      if (original) {
        original.numericValue = oldValue;
      }

      this.refresh(this.ke);
      return;
    }

    wfSignature.numericValue = value;
    wfSignature.signed = true;
    wfSignature.signedBy = this.currentUser;
    await this.approve(wfSignature.taskID, wfSignature.approveID, saveTask);
  }

  private async handleVacuumNumericChange(
    wfSignature: WfSignatureResource,
    taskId: number,
    value: number
  ): Promise<void> {
    const parsedValue = this.parseVacuumValue(value);

    const saveTask: SaveTaskResource = {
      id: taskId,
      doubleValue: parsedValue,
      field: 'doubleValue',
      formType: FormType.KE,
      option: WFSaveOptions.MANUAL,
      value: parsedValue > 0 && !wfSignature.visible ? true : wfSignature.value
    };

    wfSignature.doubleValue = parsedValue;

    this.updateVacuumWarningState(wfSignature);

    const number = this.signatures.find(s => s.id === wfSignature.id)?.number;
    const refreshedSignature = await this.getSignature(wfSignature, this.section);

    refreshedSignature.number = number;
    refreshedSignature.textValue = this.formatSciNotation(parsedValue);

    const index = this.signatures.findIndex(s => s.id === refreshedSignature.id);
    if (index >= 0) {
      this.signatures[index] = refreshedSignature;
    }

    await this.saveTask(saveTask);
  }

  private parseVacuumValue(value: number): number {
    const normalized = value
      .toString()
      .replace(/\s/g, '')
      .toLowerCase()
      .replace('x10^', 'e');

    return +normalized;
  }

  private updateVacuumWarningState(wfSignature: WfSignatureResource): void {
    const shutter = this.shutters.find(x => x.id === this.ke?.resource?.id);

    let vacuumName = '';
    if (wfSignature.titleAfter) {
      vacuumName = wfSignature.titleAfter
        .replace('<strong>', '')
        .replace('</strong> = ', '')
        .replace('<b>', '')
        .replace('</b> = ', '');
    }

    const vacuum = shutter?.vacuums.find(v => v.name === vacuumName);
    if (!vacuum) {
      return;
    }

    wfSignature.warning =
      wfSignature.doubleValue === 0 ||
      !(
        (vacuum.keVacuumMinRequirement ?? 0) <= (wfSignature.doubleValue ?? 0) &&
        (wfSignature.doubleValue ?? 0) <= (vacuum.keVacuumMaxRequirement ?? 0)
      );

    const moreInfos = [
      `Requirement:${vacuum.keVacuumMinRequirement} to ${vacuum.keVacuumMaxRequirement}`
    ];

    wfSignature.moreInfo = JSON.stringify(moreInfos);
    wfSignature.disabled =
      !!wfSignature.warning &&
      !this.currentUser?.userRole?.some(x => x.roleID === Roles.VAC);
  }

  private async handleTextInputChange(
    wfSignature: WfSignatureResource,
    taskId: number,
    value: number
  ): Promise<void> {
    const text = value.toString();

    const saveTask: SaveTaskResource = {
      id: taskId,
      text,
      field: 'text'
    };

    if (wfSignature.signed && wfSignature.text !== text) {
      wfSignature.text = text;

      const confirmed = await this.confirmWarning(
        'KEVacuumEditTextWarning',
        'warn',
        '400px'
      );

      if (confirmed && wfSignature.unapproveID) {
        await this.saveTask(saveTask);
        await this.unapprove(wfSignature.taskID, wfSignature.unapproveID);
        return;
      }

      this.refresh(this.ke);
      return;
    }

    if (wfSignature.text !== text) {
      wfSignature.text = text;
      await this.approve(wfSignature.taskID, wfSignature.approveID, saveTask);
    }
  }

  private async handleDefaultTextChange(
    wfSignature: WfSignatureResource,
    taskId: number,
    value: number
  ): Promise<void> {
    const text = value.toString();

    const saveTask: SaveTaskResource = {
      id: taskId,
      text,
      field: 'text'
    };

    wfSignature.text = text;
    await this.saveTask(saveTask);

    if (this.ke?.status === FormStatusEnum.Draft) {
      const status: SaveKEStatusResource = {
        id: this.ke.id,
        status: FormStatusEnum.Active
      };

      const data = await this._ke.putStatus(status).toPromise();
      if (data) {
        this.ke = data;
        this.alert.message('formStateChanged', [
          new MessagePlaceholder('{serialNo}', this.ke.serialNo),
          new MessagePlaceholder('{status}', data.statusName)
        ]);
      }
    }
  }

  private async confirmWarning(
    messageKey: string,
    icon: string,
    width: string
  ): Promise<boolean> {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width,
      data: {
        message: this.getMessage(messageKey).description,
        icon
      }
    });

    return !!(await dialogRef.afterClosed().toPromise());
  }

  async saveTask(saveTask: SaveTaskResource): Promise<WfTaskLocalResource | WfSignatureResource | undefined> {
    try {
      const data = await firstValueFrom(this._ke.saveWFTask(saveTask));

      if (data?.wfTaskLocal) {
        this._ke.updateTask(data.wfTaskLocal);
      }

      return data;
    } catch (error) {
      this.handleError(error);
      return;
    }
  }

  async approve(
    taskId?: number,
    actionId?: number,
    saveTask: SaveTaskResource | null = null
  ): Promise<void> {
    if (!taskId || !actionId) {
      return;
    }

    const wfSignature = this.getWfSignatureByTaskId(taskId);
    if (!wfSignature) {
      return;
    }

    const canApprove = await this.canApproveHandshake(taskId);
    if (!canApprove) {
      return;
    }

    if (saveTask) {
      await this.saveTask(saveTask);
    }

    try {
      await this._ke.sign(taskId, actionId).toPromise();

      this.markSignatureAsSigned(wfSignature);
      this._ke.updateTask(wfSignature.wfTaskLocal);

      await this.refreshWorkflowSection();
      await this.refresh(this.ke);

      if (this.shouldRefreshVacuum()) {
        this.refreshVacuum.emit();
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  async unapprove(taskId?: number, actionId?: number): Promise<void> {
    if (!taskId || !actionId) {
      return;
    }

    this.wfTaskSignature.taskId = taskId;
    this.wfTaskSignature.actionId = actionId;

    const wfSignature = this.getWfSignatureByTaskId(taskId);
    if (!wfSignature) {
      return;
    }

    this.clearSignature(wfSignature);
    this._ke.updateTask(wfSignature.wfTaskLocal);

    try {
      await this._ke.sign(taskId, actionId).toPromise();
    } catch (error) {
      this.handleError(error);
    }
  }

  private getWfSignatureByTaskId(taskId: number): WfSignatureResource | undefined {
    return this.wfSignatures.find(signature => signature.taskID === taskId);
  }

  /**
   * Confirms the final KE approval and safely handles its Handshake Olog notification.
   * @param taskId Workflow task whose optimistic approval must be reset when canceled.
   * @returns True when the approval may proceed.
   */
  private async canApproveHandshake(taskId: number): Promise<boolean> {
    if (this.section !== 5 || !this.signedAll()) {
      return true;
    }

    const confirmed = await this.ShowAproveTohandshakeMessage();
    if (!confirmed) {
      this.resetApprovalState(taskId);
      await this.refresh(this.ke);
      return false;
    }

    if (this.ke?.status !== FormStatusEnum.KeyEnable) {
      return true;
    }

    const response = await this._olog.postToOlog(
      FormType.KE,
      this.ke.id,
      OlogNotificationType.Handshake
    );

    const canContinue = response?.ok ?? false;
    if (!canContinue) {
      // Keep the checkbox consistent with the unsigned backend task when Olog is canceled.
      this.resetApprovalState(taskId);
      await this.refresh(this.ke);
    }

    return canContinue;
  }

  private markSignatureAsSigned(wfSignature: WfSignatureResource): void {
    if (!wfSignature?.wfTaskLocal?.wfTasks?.length) {
      return;
    }

    wfSignature.signed = true;
    wfSignature.signedBy = this.currentUser;
    wfSignature.signedOn = new Date();
    wfSignature.wfTaskLocal.wfTasks[0].signedBy = {
      id: this.currentUser?.id ?? null,
      name: this.currentUser?.name ?? null,
      initials: this.currentUser?.initials ?? null
    };
  }

  private clearSignature(wfSignature: WfSignatureResource): void {
    if (!wfSignature?.wfTaskLocal?.wfTasks?.length) {
      return;
    }

    wfSignature.signed = false;
    wfSignature.signedBy = null;
    wfSignature.signedOn = null;
    wfSignature.wfTaskLocal.wfTasks[0].signedBy = null;
  }

  private resetApprovalState(taskId: number): void {
    const wfSignature = this.getWfSignatureByTaskId(taskId);
    if (!wfSignature?.wfTaskLocal?.wfTasks?.length) {
      return;
    }

    wfSignature.signed = false;
    wfSignature.signedBy = null;
    wfSignature.signedOn = null;
    wfSignature.wfTaskLocal.wfTasks[0].signedBy = {
      id: null,
      name: null,
      initials: null
    };
  }

  private async refreshWorkflowSection(): Promise<void> {
    if (!this.ke) {
      return;
    }

    const section = await this._ke.getSection(this.ke.id, this.section).toPromise();
    const sections = this.ke.wfTable?.wfTableLocal?.wfSectionLocals;

    if (!section || !sections) {
      return;
    }

    const index = sections.findIndex(item => item.number === section.number);

    if (index >= 0) {
      sections.splice(index, 1);
    }

    sections.push(section);
    this.store.dispatch(new KEnableUpdate(this.ke.id, this.ke));
  }

  private shouldRefreshVacuum(): boolean {
    return this.section === 2 && this.wfSignatures.every(signature => signature.signed);
  }

  async getEpicsValue(t: WfSignatureResource): Promise<EpicsValuesResource> {
    const epicsResource = this.buildEpicsResource(t);
    const options = this.parseKeOptions(t.options);

    const shouldAutoSave =
      !t.signed && options?.option === WFSaveOptions.AUTO;

    if (shouldAutoSave && t.doubleValue !== epicsResource.vacuumValue) {
      t.doubleValue = epicsResource.vacuumValue;

      const saveTask: SaveTaskResource = {
        id: t.id,
        doubleValue: epicsResource.vacuumValue,
        field: 'doubleValue',
        formType: FormType.KE,
        option: WFSaveOptions.AUTO
      };

      await this.saveTask(saveTask);
    }

    if (shouldAutoSave) {
      return epicsResource;
    }

    return this.buildManualEpicsResult(t, epicsResource);
  }

  private buildEpicsResource(t: WfSignatureResource): EpicsValuesResource {
    const questionTemplate = t.text ?? '';
    const resourceName = this.getResourceName(t.titleAfter);
    const resource = this.resources.find(r => r.id === t.resourceId);
    const existingEpicsResource = this.epicsValues?.find(e => e.name === resourceName);

    const doubleValue = this.resolveVacuumDoubleValue(t, existingEpicsResource);
    const formattedValue = this.formatSciNotation(doubleValue) || '';

    if (existingEpicsResource) {
      const question = this.buildEpicsQuestion(
        questionTemplate,
        formattedValue,
        existingEpicsResource.location ?? ''
      );

      existingEpicsResource.question = this.disabled ? null : question;
      existingEpicsResource.isValid = this.isVacuumValueValid(
        doubleValue,
        existingEpicsResource.keVacuumMinRequirement,
        existingEpicsResource.keVacuumMaxRequirement
      );
      existingEpicsResource.vacuumValue = doubleValue ?? null;

      t.enabled = !t.signed;

      return existingEpicsResource;
    }

    if (resource) {
      return {
        id: 0,
        name: resource.name ?? '',
        type: 4,
        channelName: null,
        vacuumValue: t.doubleValue,
        location: null,
        keVacuumMinRequirement: resource.keVacuumMinRequirement ?? null,
        keVacuumMaxRequirement: resource.keVacuumMaxRequirement ?? null,
        question: null,
        isValid: this.isVacuumValueValid(
          doubleValue,
          resource.keVacuumMinRequirement,
          resource.keVacuumMaxRequirement
        )
      } as EpicsValuesResource;
    }

    return {
      id: 0,
      name: null,
      type: 4,
      channelName: null,
      vacuumValue: null,
      location: null,
      keVacuumMinRequirement: null,
      keVacuumMaxRequirement: null,
      question: null,
      isValid: false
    } as EpicsValuesResource;
  }

  private buildManualEpicsResult(
    t: WfSignatureResource,
    resource: EpicsValuesResource
  ): EpicsValuesResource {
    return {
      ...resource,
      question: t.doubleValue !== 0 ? this.formatSciNotation(t.doubleValue) : null,
      vacuumValue: resource.channelName ? t.doubleValue : null
    };
  }

  private parseKeOptions(optionsJson?: string): KEOptions | null {
    if (!optionsJson) {
      return null;
    }

    try {
      return this.utils.JSONparse(optionsJson) as KEOptions;
    } catch {
      return null;
    }
  }

  private getResourceName(titleAfter?: string): string {
    return (titleAfter ?? '')
      .replace('<b>', '')
      .replace('</b> = ', '');
  }

  private resolveVacuumDoubleValue(
    t: WfSignatureResource,
    epicsResource?: EpicsValuesResource
  ): number | null {
    if (t.signed) {
      return t.doubleValue ?? null;
    }

    if (!t.doubleValue) {
      return epicsResource?.vacuumValue ?? null;
    }

    return t.doubleValue;
  }

  private buildEpicsQuestion(
    template: string,
    formattedValue: string,
    location: string
  ): string {
    const match = template.match(/\{{(.*)\}}/);

    if (!match) {
      return template.replace('{rack}', location);
    }

    return template
      .replace(match[0], formattedValue)
      .replace('{rack}', location);
  }

  private isVacuumValueValid(
    value?: number | null,
    min?: number | null,
    max?: number | null
  ): boolean {
    if (!value || value === 0) {
      return false;
    }

    return value >= (min ?? 0) && value <= (max ?? 0);
  }

  loadEpicsValues(): void {
    this.epicsValues$ = this.store.select(state => state.Resources.epics);
    this.epicaValiesSubs = this.epicsValues$.subscribe(data => {
      this.epicsValues = data;

      this.epicsValues.forEach(epicsValue => {
        const vacuumValue = this.pvs?.find(p => p.pvName === epicsValue.channelName)?.value;
        epicsValue.vacuumValue = vacuumValue ? +vacuumValue : null;
      });
    });
  }

  async saveEpicsValues(): Promise<void> {
    this.store.dispatch(new PVRefresh());

    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('KEResetVacuumValues').description,
        icon: 'stop'
      }
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    await this.refreshAndSaveVacuum();
  }

  async refreshAndSaveVacuum(): Promise<void> {
    const saveTasks: SaveTaskResource[] = [];

    for (const signature of this.wfSignatures) {
      const saveTask = this.buildVacuumSaveTask(signature);

      if (saveTask && this.hasVacuumTaskChanged(signature, saveTask)) {
        saveTasks.push(saveTask);
      }
    }

    if (!saveTasks.length) {
      return;
    }

    try {
      await this._ke.saveWFTasks(saveTasks).toPromise();
    } catch (error) {
      this.handleError(error);
    }
  }

  private buildVacuumSaveTask(signature: WfSignatureResource): SaveTaskResource | null {
    const saveTask: SaveTaskResource = {
      id: signature.id,
      option: WFSaveOptions.AUTO,
      formType: FormType.KE
    };

    switch (signature.type) {
      case 8:
        return this.buildType8VacuumSaveTask(signature, saveTask);

      default:
        return null;
    }
  }

  private buildType8VacuumSaveTask(
    signature: WfSignatureResource,
    saveTask: SaveTaskResource
  ): SaveTaskResource | null {
    if (signature.text?.includes('EPICS')) {
      if (signature.signed || signature.text.includes('No ChannelName Found')) {
        return null;
      }

      const channelName = this.extractChannelName(signature.text);
      const resource = this.epicsValues?.find(
        epics => epics.channelName?.toUpperCase() === channelName
      );

      saveTask.field = 'doubleValue';
      saveTask.doubleValue =
        resource?.vacuumValue === 0 ? null : resource?.vacuumValue ?? null;
      saveTask.resourceId = resource?.id;

      return saveTask;
    }

    saveTask.field = 'text';
    saveTask.text = null;

    return saveTask;
  }

  private extractChannelName(text?: string): string | undefined {
    if (!text) {
      return undefined;
    }

    const tokenMatch = text.match(/\{{(.*)\}}/);
    const channelMatch = tokenMatch?.[1]?.match(/\[(.*)\]/);

    return channelMatch?.[1]?.toUpperCase();
  }

  private hasVacuumTaskChanged(
    signature: WfSignatureResource,
    saveTask: SaveTaskResource
  ): boolean {
    switch (saveTask.field) {
      case 'numericValue':
        return (saveTask.numericValue ?? 0) !== (signature.numericValue ?? 0);

      case 'doubleValue':
        return (saveTask.doubleValue ?? 0) !== (signature.doubleValue ?? 0);

      case 'text':
        return (saveTask.text ?? null) !== (signature.text ?? null);

      default:
        return false;
    }
  }

  save(saveTask: SaveTaskResource) {
    return new Promise((resolve, reject) => {
      this._ke.saveWFTask(saveTask).toPromise().then(async wfSignatureResource => {
        const index = this.ke?.wfSignatures?.findIndex(x => x.id == wfSignatureResource?.id);
        if (index && wfSignatureResource) {
          let wfSignature = this.ke?.wfSignatures?.[index];
          if (wfSignature) {
            wfSignature = wfSignatureResource;
            this._ke.updateTask(wfSignatureResource.wfTaskLocal);
            resolve(wfSignatureResource);
          }
        }
      },
        error => {
          this.handleError(error);
          reject(error);
        });
    });
  }

  async resetStatusToActive(): Promise<void> {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('KEResetToActive').description,
        icon: 'warn'
      }
    });

    dialogRef.afterClosed().toPromise().then(async data => {
      if (data && this.ke?.id) {
        const statusResource: SaveKEStatusResource = {
          id: this.ke?.id,
          status: FormStatusEnum.Active
        };

        this.alert.info(this.getMessage('formStateChanged').description.replace('{status}', 'Active').replace('{serialNo}', this.ke.serialNo));
        this.ke.status = FormStatusEnum.Active;
        this.ke.statusName = 'Active';
        await firstValueFrom(this._ke.putStatus(statusResource));
      }
    });
  }

  async skipStatusToShieldings(): Promise<void> {
    if (!this.ke?.id) {
      return;
    }

    const statusResource: SaveKEStatusResource = {
      id: this.ke.id,
      status: FormStatusEnum.Shielding
    };

    try {
      await this._ke.putStatus(statusResource).toPromise();

      this.ke.status = FormStatusEnum.Shielding;
      this.ke.statusName = 'Shielding';

      this.requestRefresh.emit(this.ke);

      this.alert.info(
        this.getMessage('formStateChanged').description
          .replace('{status}', 'Shielding')
          .replace('{serialNo}', this.ke.serialNo ?? '')
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  private async unapproveSignedTasks(): Promise<void> {
    const signedTasks = this.wfSignatures.filter(
      signature => signature.signed && signature.taskID && signature.unapproveID
    );

    await Promise.all(
      signedTasks.map(async signature => {
        this.wfTaskSignature.taskId = signature.taskID;
        this.wfTaskSignature.actionId = signature.unapproveID;

        if (signature.taskID && signature.unapproveID)
          await firstValueFrom(this._ke.sign(signature.taskID, signature.unapproveID));
      })
    );
  }

  formatSciNotation(numberToFormat?: number | null) {
    if (numberToFormat != undefined && numberToFormat != null) {
      if (numberToFormat == 0) return '0.00';
      return numberToFormat.toExponential(2).toString().replace('e', ' x 10').replace('-', '<sup>-').replace('+', '<sup>');
    }
    else return null;
  }

  async editOlog(): Promise<void> {
    if (!this.ke) return;

    try {
      const data = await firstValueFrom(
        this._olog.getOlog(
          FormType.KE,
          this.ke.id,
          OlogNotificationType.Handshake
        )
      );

      const isSaved = await firstValueFrom(
        this.dialog.open(PartialOlogComponent, {
          height: 'fit-content',
          width: '80%',
          data
        }).afterClosed()
      );

      if (!isSaved) return;

      // do something after save
      // example:
      // await this.reloadOlog();
    } catch (error) {
      console.error('Error editing Olog:', error);
    }
  }

  signedAll(): boolean {
    const ignoredComponentTypes = [2];

    const signatures =
      this.section > 3
        ? this.wfSignatures
        : this.ke?.wfSignatures?.filter(
          signature => signature.sectionNumber > 0 && signature.sectionNumber <= 3
        );

    const requiredSignatures = signatures?.filter(
      signature =>
        signature.required &&
        !ignoredComponentTypes.includes(signature.component?.type ?? 0)
    ) ?? [];

    return requiredSignatures.every(signature => signature.signed);
  }

  async ShowAproveTohandshakeMessage(): Promise<boolean> {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '500px',
      data: {
        message: this.getMessage('toHandshakeWarning').description,
        icon: 'warn'
      }
    });
    const data: boolean = await confirm.afterClosed().toPromise();
    if (data) {
      return data;
    }
    return false;
  }

  returnButton() {
    return this.ke?.status === FormStatusEnum.Active && this.signedAll() && this.isVacuumSection;
  }

  canRestartShieldings() {
    let valid = false;

    if (!this.currentUser || !this.ke || this.currentUser?.id != this.shieldingsUserId || this.shieldingsUserId === 0) {
      return valid;
    }

    if (this.isShieldingsSection && this.ke.status === FormStatusEnum.Shielding || this.section === 5 && this.ke.status === FormStatusEnum.KeyEnable) {
      if (this.hasRoles([Roles.FO])) {
        valid = true;
      }
    }
    return valid;
  }

  async resetStatusShieldings() {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('KEResetShieldings').description,
        icon: 'warn'
      },
    });
    confirm.afterClosed().subscribe(async (data) => {
      if (data) {
        this.resetSignatures();
        await this.updateStatusAndUnsignTasks();
        // this.requestRefresh.emit(this.ke);
        this.alert.info(this.getMessage('formStateChanged').description.replace('{status}', 'Shielding').replace('{serialNo}', this.ke?.serialNo ?? ''));
      }
      else return;
    });

  }

  private resetSignatures() {
    this.wfSignatures.map(x => this.resetSignature(x));
    (this.ke?.wfSignatures?.filter(x => x.sectionNumber === 4) || []).map(x => this.resetSignature(x));
  }

  private resetSignature(signature: WfSignatureResource) {
    if (signature.signed) {
      signature.signed = false;
      signature.signedBy = null;
      signature.signedOn = null;
    }
  }

  private async updateStatusAndUnsignTasks() {
    if (this.ke) {
      const statusResource: SaveKEStatusResource = {
        id: this.ke.id,
        status: FormStatusEnum.Shielding
      };

      this.alert.info(this.getMessage('formStateChanged').description.replace('{serialNo}', this.ke.serialNo).replace('{status}', 'Shielding'));
      await firstValueFrom(this._ke.putStatus(statusResource));
      // This is done in Backend now when KE is set to Shielding, but we keep it here just in case and to avoid having to reload signatures

      // if (this.ke?.wfTableID) {
      //   const dataSection4 = (await this._ke.readWFBySection(this.ke.wfTableID, 4).toPromise()) ?? [];
      //   const dataSection5 = await this._ke.readWFBySection(this.ke.wfTableID, 5).toPromise() ?? [];
      //   await this.signTasks(dataSection4);
      //   await this.signTasks(dataSection5);
      // }
    }
  }

  // private async signTasks(tasks: WfSignatureResource[]) {
  //   for (const task of tasks) {
  //     if (task.signed) {
  //       this.wfTaskSignature.taskId = task.taskID;
  //       this.wfTaskSignature.actionId = task.unapproveID;

  //       try {
  //         await this._ke.sign(task.taskID, task.unapproveID ?? 0).toPromise();
  //       } catch (error) {
  //         console.error(error);
  //       }
  //     }
  //   }
  // }


  resetSection4(): string {
    return this.section === 4 ? 'Reset Section 4' : 'Reset & Return to Section 4';
  }

  duplicated() {
    this.refresh(this.ke);
  }

  /**
   * Reevaluates conditional KE signatures and persists required-state changes.
   */
  checkConditions() {
    const tags = this.wfSignatures.filter(x => x.code).map(x => x.code);
    tags.map(tag => {
      if (tag) {
        const filteredSignatures = this.wfSignatures.filter(x => x.condition?.includes(tag));

        filteredSignatures.map(async signature => {
          if (signature.condition) {
            // Reevaluate dependent signatures with the shared parser after a tagged task changes.
            const value = ConditionParser.parseCondition(signature.condition, this._ke.allTasks);
            if (signature.required !== value) {
              signature.required = value;
              const task = signature.wfTaskLocal.wfTasks?.[0];
              if (task)
                task.required = value;
              await this._ke.saveRequiredWFTask(signature.taskID, value).toPromise();
            }
          }
        });
      }
    });
  }

  getChecked(wfSignature: WfSignatureResource): string {
    const component = this.components.find(x => x.id == wfSignature.type);
    if (component?.hasMultipleCheckboxes) {
      const storedValues = this.utils.JSONparse(wfSignature.text);
      const values = storedValues.find((x: any) => x.key == 'checked').val;
      return values.filter((x: any) => x.checked).map((y: any) => y.value).join(',');
    }
    if (component?.hasRadioButtons) {
      return wfSignature.numericValue?.toString() ?? '';
    }
    return '';
  }

  closed() {
    this.requestRefresh.emit(this.ke);
  }

}

interface KEOptions {
  user: User;
  option: WFSaveOptions;
  date: Date;
}

export interface AffectedForm {
  formType: FormType;
  formID: number;
  serialNo: string;
  formStatus: FormStatusEnum;
  radSurveyChecklists: RadSurveyChecklists[];
}

export interface RadSurveyChecklists {
  iD: number;
  serialNo: string;
  statusID: number;
}
